import moment from 'moment';
import React from 'react';

import { CalendarV2 } from '~/src/dash/components/calendar/CalendarV2';
import { INewCalDates } from '~/src/dash/components/calendar/CalendarV2/common';
import {
  CalendarIcon,
  CheckboxCheckedIcon,
  CheckboxUncheckedIcon,
} from '~/src/dash/components/icons/misc-icons';
import { Col, Cols, spacing, Text, usePanel } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { DateRange, defaultDateRanges, IDateRange } from '~/src/dash/utils/dateTimeRanges';
import { DashboardTrendsSticker } from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { DateTimeRangeToggles } from '~/src/dash/views/dashboards/DashboardHome/common/DateTimeRangeToggles';

interface IDashboardCompareToggleProps {
  isCompareEnabled: boolean;
  setIsCompareEnabled: React.Dispatch<React.SetStateAction<boolean>>;
}

const DashboardCompareToggle = ({
  isCompareEnabled,
  setIsCompareEnabled,
}: IDashboardCompareToggleProps) => {
  return (
    <div onClick={() => setIsCompareEnabled(!isCompareEnabled)}>
      <Cols style={{ alignItems: 'center', cursor: 'pointer' }} gap='4px'>
        <Col displayFlex>
          {isCompareEnabled ? (
            <CheckboxCheckedIcon color={publicaColors.darkGray} />
          ) : (
            <CheckboxUncheckedIcon color={publicaColors.darkGray} />
          )}
        </Col>
        <Col>
          <DashboardTrendsSticker isActive={isCompareEnabled} />
        </Col>
        <Col>
          <Text small bold disableSelect>
            Compare
          </Text>
        </Col>
      </Cols>
    </div>
  );
};

interface IDashboardPeriodsProps {
  calStartDate: moment.Moment;
  calEndDate: moment.Moment;
  calCompareStartDate: moment.Moment;
  calCompareEndDate: moment.Moment;
  isCompareEnabled: boolean;
  handleApplyDatesChange: ({ newStartDate, newEndDate }: INewCalDates) => void;
  handleApplyCompareDatesChange: ({ newStartDate, newEndDate }: INewCalDates) => void;
  dateRange: IDateRange;
  handleToggleDateRange: (rangeKey: DateRange) => void;
  setIsCompareEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isDisabled?: boolean;
}

export const DashboardPeriods = ({
  calStartDate,
  calEndDate,
  calCompareStartDate,
  calCompareEndDate,
  isCompareEnabled,
  handleApplyDatesChange,
  handleApplyCompareDatesChange,
  dateRange,
  handleToggleDateRange,
  setIsCompareEnabled,
  isDisabled,
}: IDashboardPeriodsProps) => {
  const { height } = usePanel();
  return (
    <Cols extraSmallVerticalPadding gap={spacing.extraLarge} style={{ alignItems: 'center' }}>
      <Col>
        <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
          <Col displayFlex>
            <CalendarIcon color={publicaColors.veryDarkGray} style={{ height: '14px' }} />
          </Col>
          <Col>
            <Text small bold>
              Period
            </Text>
          </Col>
        </Cols>
      </Col>
      <Col>
        <Cols wrap gap={spacing.base} style={{ alignItems: 'center', rowGap: spacing.extraSmall }}>
          <Col className='calendar_input_wrapper'>
            <CalendarV2
              calStartDate={calStartDate}
              calEndDate={calEndDate}
              handleApplyDatesChange={handleApplyDatesChange}
              isDisabled={isDisabled}
              uniqueID='mainCal'
            />
          </Col>
          <Col>
            <DateTimeRangeToggles
              ranges={defaultDateRanges}
              activeKey={dateRange.key}
              onClick={handleToggleDateRange}
            />
          </Col>
          <Col>
            <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
              <Col>
                <DashboardCompareToggle
                  isCompareEnabled={isCompareEnabled}
                  setIsCompareEnabled={setIsCompareEnabled}
                />
              </Col>
              <Col className='calendar_input_wrapper'>
                <CalendarV2
                  calStartDate={calCompareStartDate}
                  calEndDate={calCompareEndDate}
                  handleApplyDatesChange={handleApplyCompareDatesChange}
                  isCompareCalendar
                  endDateOffset={calEndDate
                    .clone()
                    .endOf('day')
                    .diff(calStartDate.clone().startOf('day'), 'days')}
                  maxDate={calStartDate.clone()}
                  isDisabled={isDisabled || !isCompareEnabled}
                  anchorDirection={(height || 0) > 64 ? 'left' : 'right'} // a height increase means a row break has occurred and the compare calendar input is now on the left and must change accordingly
                  uniqueID='compareCal'
                />
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Col>
    </Cols>
  );
};
